import { ClickAwayListener, TextField, Tooltip } from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DrawerAccordionSection,
  scrollToElement,
  useWorkPageStore,
  WorkLayoutRenderingMode,
} from '@diana/core';
import { SectionProps } from './Workflow.types';
import { useWorkflowSectionActions } from './useWorkflowSectionActions';
import { WORK_PAGE_NS } from '../../i18n';
import { useSectionRename } from '../../../../hooks/useSectionRename';

export function Section({
  component,
  color,
  workId,
  step,
  closeDrawer,
  index,
  work,
}: SectionProps) {
  const { t } = useTranslation(WORK_PAGE_NS);

  const {
    isFirst,
    isMobileDrawer,
    isEditMode,
    isValueValid,
    borderRadius,
    onEnterPress,
    onDoubleClick,
    handleSectionRenaming,
    handleChange,
    value,
    children,
    containerStyles,
  } = useSectionRename({
    component,
    workId,
    work,
    step,
    index,
    useSectionActions: useWorkflowSectionActions,
  });

  const editor = (
    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={handleSectionRenaming}>
      <Tooltip open={!isValueValid} placement="top" title={t('section.notAllowedCharacters')}>
        <TextField
          variant="filled"
          size="small"
          hiddenLabel
          fullWidth
          value={value}
          onChange={handleChange}
          autoComplete="off"
          onKeyUp={onEnterPress}
          autoFocus
          sx={{
            top: 6,
            ml: 2,
            '.MuiFilledInput-root': {
              borderRadius: `${borderRadius}px`,
              height: 32,
              p: 0,
              pl: 0.5,
              '> input': { pl: '10px', pt: '9px' },
            },
          }}
          InputProps={{
            disableUnderline: true,
          }}
        />
      </Tooltip>
    </ClickAwayListener>
  );

  const renderingMode = useWorkPageStore((store) => store.renderingMode);
  const activeTaskId = useWorkPageStore((store) => store.activeTaskId);
  const setActiveTaskId = useWorkPageStore((store) => store.setActiveTaskId);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();

      if (renderingMode === WorkLayoutRenderingMode.Page && activeTaskId !== step.id) {
        setActiveTaskId(step.id);
      }

      setTimeout(
        () => scrollToElement({ e, id: component.id, padding: 56, isBoundingClientRect: true }),
        10,
      );
      isMobileDrawer && closeDrawer();
    },
    [
      activeTaskId,
      closeDrawer,
      component.id,
      isMobileDrawer,
      renderingMode,
      setActiveTaskId,
      step.id,
    ],
  );

  return component?.name ? (
    <DrawerAccordionSection
      color={color}
      onTitleClick={onClick}
      onPanelDoubleClick={onDoubleClick}
      label={value}
      editor={editor}
      isEditMode={isEditMode}
      isFirst={isFirst}
      sx={{ ...containerStyles, cursor: component.canEditLabel ? 'text' : 'arrow' }}
    >
      {children}
    </DrawerAccordionSection>
  ) : null;
}
